import { tokenFilterParsers } from '@/app/collection/[idOrSlug]/components/project-tokens/utils'
import { createSearchParamsCache, parseAsBoolean } from 'nuqs/server'
import { formatDate as genericFormatDate } from '@/app/utils/common'
import { Pmp_Configs, Pmp_Param_Types_Enum, Pmps_Metadata } from '@/gql/generated/graphql'
import { hexToBool, hexToBigInt, formatUnits, hexToBytes, hexToNumber, toHex } from 'viem'

export function formatDate(date: string) {
  return genericFormatDate(
    new Date(date),
    {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    },
    'en-UK'
  )
}

const { page: _page, ...restTokenFilterParsers } = tokenFilterParsers

export const tokenPageSearchParamsParsers = {
  ...restTokenFilterParsers,
  infoOpen: parseAsBoolean.withDefault(true),
  isCustomizing: parseAsBoolean.withDefault(false),
  liveView: parseAsBoolean,
  galleryFrame: parseAsBoolean.withDefault(false),
  hideNavigationArrows: parseAsBoolean.withDefault(false)
}
export const tokenPageSearchParamsCache = createSearchParamsCache(tokenPageSearchParamsParsers)

export function getExpirationString(expirationDate: Date): string {
  const now = new Date()
  const diffMs = expirationDate.getTime() - now.getTime()
  if (diffMs <= 0) return 'Expired'

  // Convert to minutes
  const minutes = Math.round(diffMs / (1000 * 60))
  if (minutes < 60) return `Expires in ${minutes} min`

  // Convert to hours
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `Expires in ${hours} hr`

  // Convert to days
  const days = Math.round(hours / 24)
  if (days <= 13) return `Expires in ${days} day`

  // Convert to weeks
  const weeks = Math.round(days / 7)
  if (days < 30) return `Expires in ${weeks} wk`

  // Convert to months
  const months = Math.round(days / 30)
  if (months <= 11) return `Expires in ${months} mon`

  // Convert to years
  const years = Math.round(days / 365)
  return `Expires in ${years} yr`
}

// TODO: This is temporarily copied from the features package
// See https://linear.app/art-blocks/issue/PRO-1514/shared-utils-package-monorepo
export const pmpConfiguredValueAsHexString = (pmp: Pmps_Metadata): `0x${string}` => {
  return (pmp?.configured_value as `0x${string}`) || '0x00'
}

// Convert to signed integer if it's a negative number in two's complement
// For 256-bit integers, if the value is >= 2^255, it's negative
export const BIT_255 = BigInt(1) << BigInt(255)
export const BIT_256 = BigInt(1) << BigInt(256)

// Constants for range checking
const PMP_TIMESTAMP_MIN = BigInt(0)
const PMP_TIMESTAMP_MAX = BigInt('0xFFFFFFFFFFFFFFFF') // 2^64 - 1
const PMP_HEX_COLOR_MIN = BigInt(0)
const PMP_HEX_COLOR_MAX = BigInt('0xFFFFFF')

export const formatConfiguredPMPValue = (pmp: Pmps_Metadata, pmpConfig: Pmp_Configs): string | boolean | null => {
  // switch on the param_type to properly interpret the pmp value
  switch (pmpConfig.param_type) {
    case Pmp_Param_Types_Enum.Bool:
      // bool is false if zero, true otherwise
      return hexToBool(pmpConfiguredValueAsHexString(pmp)) || false
    case Pmp_Param_Types_Enum.Uint256Range: {
      // parse hex string to large number string
      const configuredValue = hexToBigInt(pmpConfiguredValueAsHexString(pmp))
      // Convert hex ranges to signed integers
      const minRange = pmpConfig.min_range ? BigInt(pmpConfig.min_range) : BigInt(0)
      const maxRange = pmpConfig.max_range ? BigInt(pmpConfig.max_range) : BigInt(0)

      // Convert to signed integers if they're in two's complement
      const minSigned = minRange >= BIT_255 ? minRange - BIT_256 : minRange
      const maxSigned = maxRange >= BIT_255 ? maxRange - BIT_256 : maxRange

      if (configuredValue < minSigned || configuredValue > maxSigned) {
        return null
      }
      return configuredValue.toString()
    }
    case Pmp_Param_Types_Enum.DecimalRange: {
      // parse hex string to large number string in decimal format, assuming 10 decimal places
      const configuredValue = hexToBigInt(pmpConfiguredValueAsHexString(pmp))
      // Convert hex ranges to signed integers
      const minRange = pmpConfig.min_range ? BigInt(pmpConfig.min_range) : BigInt(0)
      const maxRange = pmpConfig.max_range ? BigInt(pmpConfig.max_range) : BigInt(0)

      // Convert to signed integers if they're in two's complement
      const minSigned = minRange >= BIT_255 ? minRange - BIT_256 : minRange
      const maxSigned = maxRange >= BIT_255 ? maxRange - BIT_256 : maxRange

      if (configuredValue < minSigned || configuredValue > maxSigned) {
        return null
      }
      const formattedValue = formatUnits(configuredValue, 10)
      return formattedValue
    }
    case Pmp_Param_Types_Enum.Int256Range: {
      // parse hex string to large number string in signed integer format
      const unsignedValue = hexToBigInt(pmpConfiguredValueAsHexString(pmp))

      // Convert to signed integer if it's a negative number in two's complement
      // For 256-bit integers, if the value is >= 2^255, it's negative
      const BIT_255 = BigInt(1) << BigInt(255)
      const BIT_256 = BigInt(1) << BigInt(256)

      // Check if the most significant bit is set (indicates negative in two's complement)
      const configuredValue =
        unsignedValue >= BIT_255
          ? unsignedValue - BIT_256 // Convert to negative representation
          : unsignedValue
      // Convert hex ranges to signed integers
      const minRange = pmpConfig.min_range ? BigInt(pmpConfig.min_range) : BigInt(0)
      const maxRange = pmpConfig.max_range ? BigInt(pmpConfig.max_range) : BigInt(0)

      // Convert to signed integers if they're in two's complement
      const minSigned = minRange >= BIT_255 ? minRange - BIT_256 : minRange
      const maxSigned = maxRange >= BIT_255 ? maxRange - BIT_256 : maxRange

      if (configuredValue < minSigned || configuredValue > maxSigned) {
        return null
      }
      return configuredValue.toString()
    }
    case Pmp_Param_Types_Enum.Timestamp: {
      // parse hex string to large number, converting to timestamp format
      const configuredValue = hexToBigInt(pmpConfiguredValueAsHexString(pmp))
      if (configuredValue < PMP_TIMESTAMP_MIN || configuredValue > PMP_TIMESTAMP_MAX) {
        return null
      }
      return configuredValue.toString()
    }
    case Pmp_Param_Types_Enum.HexColor: {
      // range check happens before formatting to hex color
      const configuredValue = hexToBigInt(pmpConfiguredValueAsHexString(pmp))
      if (configuredValue < PMP_HEX_COLOR_MIN || configuredValue > PMP_HEX_COLOR_MAX) {
        return null
      }
      // parse hex string to large number, converting to hex color format
      const rgb = Array.from(hexToBytes(pmpConfiguredValueAsHexString(pmp)).slice(-3))
      const color =
        '#' +
        rgb
          .map((byte) => byte.toString(16).padStart(2, '0'))
          .join('')
          .padStart(6, '0')
      return color
    }
    case Pmp_Param_Types_Enum.Select: {
      // parse hex string to number, converting to select option index
      const index = hexToNumber(pmpConfiguredValueAsHexString(pmp))
      const selectOptions = pmpConfig.select_options || []
      // range check happens before selecting the option
      if (index < 0 || index >= selectOptions.length) {
        return null
      }
      return selectOptions[index]
    }
    case Pmp_Param_Types_Enum.String: {
      // assign artist assigned string if populated
      if (pmp.artist_configured_value_string && pmp.artist_configured_value_string.length > 0) {
        return pmp.artist_configured_value_string
      }
      // assign non-artist assigned string if populated
      if (pmp.non_artist_configured_value_string && pmp.non_artist_configured_value_string.length > 0) {
        return pmp.non_artist_configured_value_string
      }
      // otherwise skip
      return null
    }
  }
  // @dev if we don't match any of the cases, return null
  return null
}

type PmpTokenParamsConfiguredValue = {
  paramType: number
  configuredValue: `0x${string}`
}

export const formatPmpTokenParamsInput = (
  updatedValue: string,
  pmpConfig: Pmp_Configs
): PmpTokenParamsConfiguredValue => {
  // Format the value based on param type
  let paramType = 0 // Default to Unconfigured
  let configuredValue = '0x0000000000000000000000000000000000000000000000000000000000000000' as `0x${string}`

  let unsignedVal
  let cleanHex
  let paddedHex
  let tmpVal
  let selectedIndex
  switch (pmpConfig.param_type) {
    case Pmp_Param_Types_Enum.HexColor:
      paramType = 6
      cleanHex = updatedValue.replace('#', '')
      paddedHex = cleanHex.padStart(64, '0')
      configuredValue = `0x${paddedHex}` as `0x${string}`
      break
    case Pmp_Param_Types_Enum.String:
      paramType = 8
      break
    case Pmp_Param_Types_Enum.Uint256Range:
      paramType = 3
      configuredValue = toHex(BigInt(Math.floor(Number(updatedValue))), { size: 32 }) as `0x${string}`
      break
    case Pmp_Param_Types_Enum.Int256Range:
      paramType = 4
      unsignedVal = BigInt(Math.floor(Number(updatedValue)))
      // For negative numbers, convert from regular integer to two's complement
      tmpVal = unsignedVal < BigInt(0) ? BIT_256 + unsignedVal : unsignedVal
      configuredValue = toHex(tmpVal, { size: 32 }) as `0x${string}`
      break
    case Pmp_Param_Types_Enum.DecimalRange:
      paramType = 5
      // multiply by 1e10 for decimal precision
      configuredValue = toHex(BigInt(Math.floor(Number(updatedValue) * 1e10)), { size: 32 }) as `0x${string}`
      break
    case Pmp_Param_Types_Enum.Bool:
      paramType = 2
      configuredValue =
        updatedValue === 'true'
          ? ('0x0000000000000000000000000000000000000000000000000000000000000001' as `0x${string}`)
          : ('0x0000000000000000000000000000000000000000000000000000000000000000' as `0x${string}`)
      break
    case Pmp_Param_Types_Enum.Timestamp:
      paramType = 7
      configuredValue = toHex(BigInt(Math.floor(Number(updatedValue))), { size: 32 }) as `0x${string}`
      break
    case Pmp_Param_Types_Enum.Select:
      paramType = 1
      selectedIndex = pmpConfig.select_options?.findIndex((option) => option === updatedValue) ?? 0
      configuredValue = toHex(BigInt(selectedIndex), { size: 32 }) as `0x${string}`
      break
  }

  return {
    paramType,
    configuredValue
  }
}
